import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { toast } from 'sonner';
import { get, post, ApiError } from '@/lib/api';
import { formatRelative } from '@/lib/time';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { SchoolDrawer, QualityBar, ReadinessBadge } from '@/components/domain/SchoolDrawer';
import { type School, coachesOf, stateOf } from '@/lib/school';

// Schools — Scout parity (workstream G). Full 16.9k-school table served
// through the console's scout proxy (scout-dashboard's in-memory cache):
// server-side search/filter/sort/pagination, quality + readiness columns,
// CSV export of the active filter set, school create, and a detail drawer
// with edit + coach/AD tracking.

const LIMIT = 50;

type ListPayload = { schools: School[]; total: number; limit: number; offset: number };
type StatesPayload = { states: { state: string; schoolCount: number }[]; total: number };
type CountiesPayload = { state: string; counties: { county: string; schoolCount: number }[]; total: number };
type CreatePayload = { success: boolean; ghostfit_id: string; school: School };

function errMsg(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { message?: string; error?: string } | null;
    return body?.message || body?.error || `Request failed (${e.status})`;
  }
  return e instanceof Error ? e.message : String(e);
}

const selectCls = 'h-8 rounded-sm border border-border bg-surface px-2.5 text-[12px] text-text';

type Duplicate = { ghostfit_id: string; name: string; city?: string };

function AddSchoolDialog({ open, onOpenChange, onCreated }: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated: (placeId: string | null) => void;
}) {
  const [form, setForm] = useState({ name: '', state: '', city: '', county: '', mascot: '' });
  const [dups, setDups] = useState<Duplicate[]>([]);
  const [forceCreate, setForceCreate] = useState(false);
  const [busy, setBusy] = useState(false);

  function reset() {
    setForm({ name: '', state: '', city: '', county: '', mascot: '' });
    setDups([]);
    setForceCreate(false);
  }

  async function submit() {
    if (!form.name.trim() || form.state.trim().length !== 2) {
      toast.error('Name and a 2-letter state are required');
      return;
    }
    setBusy(true);
    try {
      const body: Record<string, unknown> = {
        name: form.name.trim(),
        state: form.state.trim().toUpperCase(),
      };
      if (form.city.trim()) body.city = form.city.trim();
      if (form.county.trim()) body.county = form.county.trim();
      if (form.mascot.trim()) body.mascot = form.mascot.trim();
      if (forceCreate) body.force_create = true;
      const res = await post<CreatePayload>('/api/scout/schools', body);
      toast.success(`Created ${res.school?.name || form.name.trim()}`);
      reset();
      onOpenChange(false);
      onCreated(res.school?.placeId || null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        const body = e.body as { error?: string; possible_duplicates?: Duplicate[]; ghostfit_id?: string } | null;
        if (body?.possible_duplicates?.length) {
          // Surface scout's fuzzy-dup candidates; the operator can override.
          setDups(body.possible_duplicates);
        } else {
          toast.error(`School already exists (${body?.ghostfit_id || 'exact match'})`);
        }
      } else {
        toast.error(errMsg(e));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) { if (!o) reset(); onOpenChange(o); } }}>
      <DialogContent className="max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Add school</DialogTitle>
          <DialogDescription>
            Creates a school in the Scout database. Scout checks for duplicates and assigns the ID.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2.5">
          <div className="col-span-2">
            <label htmlFor="add-school-name" className="sr-only">School name</label>
            <Input id="add-school-name" placeholder="School name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <label htmlFor="add-school-state" className="sr-only">State</label>
          <Input id="add-school-state" placeholder="State (e.g. MN) *" maxLength={2} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} />
          <label htmlFor="add-school-city" className="sr-only">City</label>
          <Input id="add-school-city" placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          <label htmlFor="add-school-county" className="sr-only">County</label>
          <Input id="add-school-county" placeholder="County" value={form.county} onChange={(e) => setForm({ ...form, county: e.target.value })} />
          <label htmlFor="add-school-mascot" className="sr-only">Mascot</label>
          <Input id="add-school-mascot" placeholder="Mascot" value={form.mascot} onChange={(e) => setForm({ ...form, mascot: e.target.value })} />
        </div>
        {dups.length > 0 && (
          <div className="rounded-sm bg-warning/10 px-2.5 py-2 text-[11.5px] text-warning">
            <p className="mb-1 font-medium">Possible duplicates:</p>
            {dups.map((d) => (
              <p key={d.ghostfit_id}>{d.name}{d.city ? ` — ${d.city}` : ''}</p>
            ))}
            <label className="mt-1.5 flex items-center gap-1.5 text-text">
              <input type="checkbox" checked={forceCreate} onChange={(e) => setForceCreate(e.target.checked)} />
              Create anyway
            </label>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" disabled={busy} onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={busy || (dups.length > 0 && !forceCreate)} onClick={submit}>
            {busy ? 'Creating…' : 'Create school'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const GRID = 'grid grid-cols-[minmax(200px,2fr)_minmax(90px,1fr)_130px_110px_minmax(110px,1.2fr)_60px_80px] items-center gap-3';

export default function Schools() {
  const [state, setState] = useState('');
  const [county, setCounty] = useState('');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [sort, setSort] = useState('quality');
  const [offset, setOffset] = useState(0);
  const [drawerId, setDrawerId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  // Server-side search — debounce keystrokes before they hit the proxy.
  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search.trim()); setOffset(0); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const filterQs = useMemo(() => {
    const qs = new URLSearchParams();
    if (state) qs.set('state', state);
    if (county) qs.set('county', county);
    if (debounced) qs.set('search', debounced);
    return qs;
  }, [state, county, debounced]);

  const listQs = useMemo(() => {
    const qs = new URLSearchParams(filterQs);
    if (sort) qs.set('sort', sort);
    qs.set('limit', String(LIMIT));
    qs.set('offset', String(offset));
    return qs.toString();
  }, [filterQs, sort, offset]);

  const { data, error, isLoading, mutate } = useSWR<ListPayload>(
    `/api/scout/schools?${listQs}`, get, { keepPreviousData: true },
  );
  const { data: statesData } = useSWR<StatesPayload>('/api/scout/states', get);
  const { data: countiesData } = useSWR<CountiesPayload>(
    state ? `/api/scout/counties?state=${encodeURIComponent(state)}` : null, get,
  );

  const states = useMemo(
    () => [...(statesData?.states || [])].sort((a, b) => a.state.localeCompare(b.state)),
    [statesData],
  );
  const counties = useMemo(
    () => [...(countiesData?.counties || [])].sort((a, b) => a.county.localeCompare(b.county)),
    [countiesData],
  );

  const total = data?.total ?? 0;
  const schools = data?.schools || [];
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + LIMIT, total);

  function pickState(next: string) {
    setState(next);
    setCounty('');
    setOffset(0);
  }

  return (
    <div className="px-7 py-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-[20px] font-semibold tracking-tighter">Schools</h1>
          <p className="mb-3 text-[12px] text-muted">
            Scout database · {total ? `${total.toLocaleString()} schools` : 'loading…'}
            {(state || county || debounced) && total ? ' match the filters' : ''}
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => { window.location.href = `/api/scout/export.csv${filterQs.size ? `?${filterQs}` : ''}`; }}
          >
            Export CSV
          </Button>
          <Button size="sm" onClick={() => setAddOpen(true)}>Add school</Button>
        </div>
      </div>

      <div className="mb-3 flex max-w-[860px] gap-2">
        <select value={state} onChange={(e) => pickState(e.target.value)} className={selectCls}>
          <option value="">All states</option>
          {states.map((s) => (
            <option key={s.state} value={s.state}>{s.state} ({s.schoolCount.toLocaleString()})</option>
          ))}
        </select>
        <select
          value={county}
          onChange={(e) => { setCounty(e.target.value); setOffset(0); }}
          className={selectCls}
          disabled={!state}
          title={state ? undefined : 'Pick a state first'}
        >
          <option value="">{state ? 'All counties' : 'County'}</option>
          {counties.map((c) => (
            <option key={c.county} value={c.county}>{c.county} ({c.schoolCount})</option>
          ))}
        </select>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, city, mascot, AD…"
          className="flex-1"
        />
        <select value={sort} onChange={(e) => { setSort(e.target.value); setOffset(0); }} className={selectCls}>
          <option value="quality">Sort: Quality</option>
          <option value="name">Sort: Name</option>
          <option value="coaches">Sort: Coaches</option>
        </select>
      </div>

      <div className="rounded-md bg-surface shadow-card">
        <div className={`${GRID} border-b border-border px-3 py-2 text-[10.5px] uppercase tracking-[0.04em] text-muted`}>
          <span>School</span>
          <span>County</span>
          <span>Quality</span>
          <span>Readiness</span>
          <span>Athletic director</span>
          <span className="text-right">Coaches</span>
          <span className="text-right">Verified</span>
        </div>

        {error ? (
          <div className="p-8 text-center text-[12px] text-muted">
            <p className="mb-2">Couldn’t reach the Scout database — {errMsg(error)}</p>
            <Button variant="secondary" size="sm" onClick={() => mutate()}>Retry</Button>
          </div>
        ) : isLoading && !data ? (
          <div className="space-y-2 p-3">
            <Skeleton className="h-7 w-full" />
            <Skeleton className="h-7 w-full" />
            <Skeleton className="h-7 w-full" />
          </div>
        ) : schools.length ? (
          schools.map((s) => (
            <button
              key={s.placeId || s.ghostfit_id}
              type="button"
              onClick={() => s.placeId && setDrawerId(s.placeId)}
              className={`${GRID} w-full border-b border-divider px-3 py-2 text-left last:border-b-0 hover:bg-bg/60`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  {s.primary_color && <span title={s.primary_color} className="h-3 w-3 shrink-0 rounded-full border border-border" style={{ background: s.primary_color }} />}
                  {s.secondary_color && <span title={s.secondary_color} className="-ml-0.5 h-3 w-3 shrink-0 rounded-full border border-border" style={{ background: s.secondary_color }} />}
                  <span className="truncate text-[12px]">{s.name || 'Unnamed school'}</span>
                </div>
                <div className="truncate text-[10.5px] text-muted">
                  {[s.city, stateOf(s), s.mascot ? `“${s.mascot}”` : null].filter(Boolean).join(' · ')}
                </div>
              </div>
              <span className="truncate text-[11.5px] text-muted">{(s.county || '').replace(/ County$/i, '') || '—'}</span>
              <QualityBar school={s} />
              <ReadinessBadge school={s} />
              <span className="truncate text-[11.5px]">{s.athletic_director?.name || <span className="text-subtle">—</span>}</span>
              <span className="text-right font-mono text-[11.5px] tabular-nums text-muted">{coachesOf(s).length}</span>
              <span className="text-right text-[10.5px] text-subtle" title={s.last_verified_at}>
                {s.last_verified_at ? formatRelative(s.last_verified_at) : '—'}
              </span>
            </button>
          ))
        ) : (
          <div className="p-8 text-center text-[12px] text-muted">No schools match the filters.</div>
        )}

        <div className="flex items-center justify-between border-t border-border px-3 py-2">
          <span className="text-[11px] text-muted">
            {total ? `${from.toLocaleString()}–${to.toLocaleString()} of ${total.toLocaleString()}` : '—'}
          </span>
          <div className="flex gap-1.5">
            <Button variant="secondary" size="sm" disabled={offset === 0 || isLoading} onClick={() => setOffset(Math.max(0, offset - LIMIT))}>
              ← Prev
            </Button>
            <Button variant="secondary" size="sm" disabled={to >= total || isLoading} onClick={() => setOffset(offset + LIMIT)}>
              Next →
            </Button>
          </div>
        </div>
      </div>

      <SchoolDrawer
        open={drawerId != null}
        onClose={() => setDrawerId(null)}
        placeId={drawerId}
        onChanged={() => mutate()}
      />
      <AddSchoolDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreated={(placeId) => {
          mutate();
          if (placeId) setDrawerId(placeId);
        }}
      />
    </div>
  );
}
